import type { FC, ReactNode } from 'react';
import { Navigate, Route, Routes as RouterRoutes, useLocation } from 'react-router';

import RoleProvider from '@zen/Auth/RoleProvider';
import RouteAuthenticator from '@zen/Auth/RouteAuthenticator';
import UserDataProvider from '@zen/Auth/UserDataProvider';
import { authRoutes, dashboardRoutes, downloadRoutes } from '@zen/Routes';
import { Env } from '@zen/types';
import loadable from '@zen/utils/component/loadable';

import DashboardContainer from '../DashboardContainer';
import { useCatchAuthError } from './hooks/useCatchAuthError';
import RouteError from './RouteError';

interface Props {
  env: Env;
}

const AccountActivationPage = loadable(() => import('@zen/Accounts/AccountActivationPage'));
const AccountSwitcher = loadable(() => import('@zen/Components/AccountSwitcher'));
const DownloadPage = loadable(() => import('@zen/DownloadPage'));
const OutdatedBrowser = loadable(() => import('@zen/Components/OutdatedBrowser'));
const PageNotFound = loadable(() => import('@zen/Components/PageNotFound'));

export const AppRoutes: FC<Props> = ({ env }) => {
  const { isError, errorMessage } = useCatchAuthError();

  // we need to keep useLocation here even if it's not used because otherwise,
  // user is not Navigateed to the dashboard page after successful login
  useLocation();

  const routes: ReactNode = (
    <RouterRoutes>
      <Route element={<RouteAuthenticator element={<DashboardContainer />} />} path={dashboardRoutes.dashboard.path} />
      <Route
        element={
          <RouteAuthenticator
            element={
              <UserDataProvider>
                <RoleProvider>
                  <AccountSwitcher />
                </RoleProvider>
              </UserDataProvider>
            }
          />
        }
        path={authRoutes.accountSwitcher.path}
      />
      <Route element={<RouteAuthenticator element={<AccountActivationPage />} />} path={authRoutes.accountActivation.path} />
      <Route element={<RouteAuthenticator element={<DownloadPage />} />} path={downloadRoutes.index.path} />
      <Route element={<Navigate to={dashboardRoutes.dashboard.getUrl()} />} path="/" />
      <Route element={<Navigate to={dashboardRoutes.dashboard.getUrl()} />} path={authRoutes.login.getUrl()} />
      <Route element={<PageNotFound />} path="*" />
    </RouterRoutes>
  );

  if (isError) {
    return <RouteError message={errorMessage} />;
  }

  return (
    <div className="antialiased">
      <OutdatedBrowser />
      {routes}
    </div>
  );
};

export default AppRoutes;
